import { useTranslate } from 'ra-core';
import { useMemo, useState } from 'react';
import { type CsvRecord, type ProcessRow, type RowProcessor } from './types';
import { useBulkTask } from './useBulkTask';
import { type ZodCsvSchema } from './bulkZodUtils';
import { ExportSheetButton } from './ExportSheetButton';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { Button } from '../../components/ui/button';
import { BulkOperationDialogFooter } from './BulkOperationDialogFooter';

export interface BulkOperationDialogProcessProps<T extends CsvRecord> {
  label: string;
  schema?: ZodCsvSchema<T>;
  data: T[];
  processRow: RowProcessor<T>;
  processingRateLimitDelay: number;
  onNext: () => void;
}

export function BulkOperationDialogProcess<T extends CsvRecord>({
  label,
  data,
  processRow,
  processingRateLimitDelay,
  onNext,
}: BulkOperationDialogProcessProps<T>) {
  const translate = useTranslate();
  const [startTime] = useState(() => new Date());

  const [processingState, setProcessingState] = useState(() =>
    data.map<ProcessRow<T>>((row, index) => ({
      status: 'idle',
      data: row,
      errors: [],
      id: index,
    }))
  );

  useBulkTask({
    setData: setProcessingState,
    task: processRow,
    taskRateLimitDelay: processingRateLimitDelay,
  });

  const progress = useMemo(() => {
    const total = processingState.length;
    let success = 0;
    let pending = 0;
    let idle = 0;
    let error = 0;

    for (const row of processingState) {
      switch (row.status) {
        case 'success':
          success++;
          break;
        case 'error':
          error++;
          break;
        case 'pending':
          pending++;
          break;
        case 'idle':
          idle++;
      }
    }

    return {
      total,
      notDone: pending + idle,
      success,
      error,
      percentageDone: total === 0 ? 100 : ((success + error) / total) * 100,
      percentageNotIdle:
        total === 0 ? 100 : ((success + error + pending) / total) * 100,
    };
  }, [processingState]);

  const estimatedTimeRemaining = useMemo(() => {
    if (progress.notDone === 0) {
      return '';
    } else if (progress.notDone === progress.total) {
      return translate('moonstar.bulk_operation.process.calculating', {
        _: 'Calculating time remaining...',
      });
    }

    const elapsedTime = new Date().getTime() - startTime.getTime();
    const estimatedTotalTime =
      (elapsedTime / (progress.total - progress.notDone)) * progress.total;
    const timeRemaining = estimatedTotalTime - elapsedTime;

    if (timeRemaining < 0) {
      return translate('moonstar.bulk_operation.process.unknown', {
        _: 'Unknown time remaining',
      });
    } else if (timeRemaining < 1_000) {
      return translate('moonstar.bulk_operation.process.lessThanSecond', {
        _: 'Less than 1 second remaining',
      });
    } else if (timeRemaining < 15_000) {
      return translate('moonstar.bulk_operation.process.fewSeconds', {
        _: 'A few seconds remaining',
      });
    } else if (timeRemaining < 90_000) {
      return translate('moonstar.bulk_operation.process.aboutMinute', {
        _: 'About a minute remaining',
      });
    } else if (timeRemaining < 3_600_000) {
      const count = Math.floor(timeRemaining / 60_000);
      return translate('moonstar.bulk_operation.process.aboutMinutes', {
        count,
        _: `About ${count} minutes remaining`,
      });
    }

    const count = Math.floor(timeRemaining / 3_600_000);
    return translate('moonstar.bulk_operation.process.aboutHours', {
      count,
      _: `About ${count} hours remaining`,
    });
  }, [startTime, progress, translate]);

  return (
    <>
      <div className="p-6">
        {progress.notDone > 0 ? (
          <div className="space-y-3">
            <div
              className="relative h-2 w-full overflow-hidden rounded-full bg-muted"
              role="progressbar"
              aria-label={translate(
                'moonstar.bulk_operation.process.progress',
                {
                  _: 'Processing progress',
                }
              )}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(progress.percentageDone)}
            >
              <div
                className="absolute inset-y-0 left-0 bg-primary/30 transition-all"
                style={{ width: `${progress.percentageNotIdle}%` }}
              />
              <div
                className="relative h-full bg-primary transition-all"
                style={{ width: `${progress.percentageDone}%` }}
              />
            </div>
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>{progress.percentageDone.toFixed(0)}%</span>
              <span>{estimatedTimeRemaining}</span>
            </div>
          </div>
        ) : progress.success === progress.total ? (
          <Alert className="border-emerald-500/50 bg-emerald-500/10 text-emerald-800 dark:text-emerald-200">
            <AlertTitle>
              {translate('moonstar.bulk_operation.process.successTitle', {
                _: 'Success',
              })}
            </AlertTitle>
            <AlertDescription>
              {translate('moonstar.bulk_operation.process.successMessage', {
                success: progress.success,
                total: progress.total,
                _: `${progress.success}/${progress.total} records were processed successfully. You can now close this dialog.`,
              })}
            </AlertDescription>
          </Alert>
        ) : progress.success > 0 ? (
          <Alert className="border-amber-500/50 bg-amber-500/10 text-amber-800 dark:text-amber-200">
            <AlertTitle>
              {translate('moonstar.bulk_operation.process.partialTitle', {
                _: 'Partial success',
              })}
            </AlertTitle>
            <AlertDescription>
              {translate('moonstar.bulk_operation.process.partialMessage', {
                success: progress.success,
                total: progress.total,
                error: progress.error,
                _: `${progress.success}/${progress.total} records were processed successfully, but ${progress.error} records failed to process. Please export the failed records, check the reasons within, and try again.`,
              })}
            </AlertDescription>
          </Alert>
        ) : (
          <Alert variant="destructive">
            <AlertTitle>
              {translate('moonstar.bulk_operation.process.errorTitle', {
                _: 'Error',
              })}
            </AlertTitle>
            <AlertDescription>
              {translate('moonstar.bulk_operation.process.errorMessage', {
                _: 'All records failed to process. Please export the failed records, check the reasons within, and try again.',
              })}
            </AlertDescription>
          </Alert>
        )}
      </div>
      <BulkOperationDialogFooter>
        {progress.notDone === 0 && progress.error > 0 && (
          <ExportSheetButton
            label="moonstar.bulk_operation.actions.exportFailed"
            getSheet={() => ({
              data: processingState
                .filter((row) => row.status === 'error')
                .map((row) => ({
                  ...row.data,
                  errorMessage: row.errors.join(', '),
                })),
              basename: `${translate(label, { _: label })} - Failed`,
            })}
            variant="destructive"
          />
        )}
        <Button type="button" disabled={progress.notDone > 0} onClick={onNext}>
          {translate('moonstar.bulk_operation.actions.done', { _: 'Done' })}
        </Button>
      </BulkOperationDialogFooter>
    </>
  );
}
